import { BadgeCheck, Check, CircleSlash, Copy, Undo2 } from 'lucide-react'

import { ActionInfo, Button } from '@krp/ui-kit'

import { splitByWords } from '../model/words'
import type { CopySide, DuplicateItem, DuplicatesActions, MatchKind, PairView } from '../model/types'
import { ErrorBlock } from './ErrorBlock'
import './duplicates.css'

const MATCH_TEXT: Readonly<Record<MatchKind, string>> = {
  lexical: 'по словам',
  semantic: 'по смыслу',
  both: 'по словам и по смыслу',
}

type CardRole = 'copy' | 'original' | null

/** Наименование дословно; общие слова пары — жирным и подчёркнутыми: видно и без цвета. */
function Name({ name, shared }: { name: string; shared: readonly string[] }) {
  return (
    <p className="dup-card__name">
      {splitByWords(name, shared).map((part, index) => (part.shared ? <strong key={index}>{part.text}</strong> : part.text))}
    </p>
  )
}

/**
 * Карточка товара пары — целиком кнопка выбора (И-6): клик, тап, Enter и Пробел. Выбранная: рамка толще, значок и слово
 * «Выбран». Отмеченная пара: «Копия» или «Оригинал» значком и словом.
 */
function ItemCard({
  item,
  shared,
  selected,
  role,
  gone,
  disabled,
  onSelect,
}: {
  item: DuplicateItem
  shared: readonly string[]
  selected: boolean
  role: CardRole
  gone: boolean
  disabled: boolean
  onSelect: () => void
}) {
  return (
    <ActionInfo id="duplicates.source.select-card" label={item.name} layout="block">
      <button type="button" className="dup-card" aria-pressed={selected} disabled={disabled} onClick={onSelect}>
        <Name name={item.name} shared={shared} />
        <dl className="dup-card__facts">
          {item.code !== null && (
            <>
              <dt>Код</dt>
              <dd>{item.code}</dd>
            </>
          )}
          {item.sku !== null && (
            <>
              <dt>Артикул</dt>
              <dd>{item.sku}</dd>
            </>
          )}
          {item.barcode !== null && (
            <>
              <dt>Штрихкод</dt>
              <dd>
                {item.barcode}
                {item.moreBarcodes > 0 && ` и ещё ${item.moreBarcodes}`}
              </dd>
            </>
          )}
          {item.group !== null && (
            <>
              <dt>Группа</dt>
              <dd>{item.group}</dd>
            </>
          )}
        </dl>
        <span className="dup-card__marks">
          {selected && (
            <span className="dup-card__mark">
              <Check size={14} aria-hidden="true" />
              Выбран
            </span>
          )}
          {role === 'copy' && (
            <span className="dup-card__mark">
              <Copy size={14} aria-hidden="true" />
              Копия
            </span>
          )}
          {role === 'original' && (
            <span className="dup-card__mark">
              <BadgeCheck size={14} aria-hidden="true" />
              Оригинал
            </span>
          )}
          {gone && <span className="dup-card__mark">Товара больше нет в источнике</span>}
          {item.markedCopyInPairId !== null && <span className="dup-card__mark">Уже отмечен копией в паре № {item.markedCopyInPairId}</span>}
        </span>
      </button>
    </ActionInfo>
  )
}

const roleOf = (side: 'first' | 'second', copySide: CopySide | null): CardRole => (copySide === null ? null : copySide === side ? 'copy' : 'original')

/**
 * Пара: две карточки и процент между ними (на телефоне — одна под другой), решения человека внизу. «Это копия» активна только
 * при выбранной карточке (И-7); «Не дубли» — всегда (И-8); у отмеченной — «Снять отметку», у «Не дубли» — «Вернуть в список».
 */
export function PairRow({ view, actions }: { view: PairView; actions: DuplicatesActions }) {
  const { pair, selectedKey, busy, error } = view
  const decided = pair.state !== 'undecided'
  const alreadyCopy =
    pair.state === 'marked' && selectedKey !== null && roleOf(selectedKey === pair.first.key ? 'first' : 'second', pair.copySide) === 'copy'
  const reason = selectedKey === null ? 'Сначала выберите товар в паре: щёлкните по его карточке.' : alreadyCopy ? 'Этот товар уже отмечен копией.' : null
  const stateText = pair.state === 'marked' ? 'Отмечена' : pair.state === 'not-duplicate' ? 'Не дубли' : 'Не разобрана'
  return (
    <li className="dup-pair" data-state={pair.state} aria-label={`Пара № ${pair.id}: ${pair.first.name} и ${pair.second.name}`}>
      <span className="dup-pair__state">
        {pair.state === 'marked' ? <Copy size={14} aria-hidden="true" /> : pair.state === 'not-duplicate' ? <CircleSlash size={14} aria-hidden="true" /> : null}
        {stateText}
      </span>
      <div className="dup-pair__cards">
        <ItemCard
          item={pair.first}
          shared={pair.sharedWords}
          selected={selectedKey === pair.first.key}
          role={roleOf('first', pair.copySide)}
          gone={pair.firstGone}
          disabled={busy || pair.state === 'not-duplicate'}
          onSelect={() => actions.cardSelected(pair.id, pair.first.key)}
        />
        <div className="dup-pair__percent">
          <strong>{pair.percent} %</strong>
          <progress value={pair.percent} max={100} aria-label={`Совпадение ${pair.percent} %`} />
          <span>{MATCH_TEXT[pair.matchedBy]}</span>
        </div>
        <ItemCard
          item={pair.second}
          shared={pair.sharedWords}
          selected={selectedKey === pair.second.key}
          role={roleOf('second', pair.copySide)}
          gone={pair.secondGone}
          disabled={busy || pair.state === 'not-duplicate'}
          onSelect={() => actions.cardSelected(pair.id, pair.second.key)}
        />
      </div>
      <div className="dup-pair__actions">
        {pair.state !== 'not-duplicate' && (
          <Button
            variant="primary"
            icon={<Copy size={16} aria-hidden="true" />}
            busy={busy}
            disabled={reason !== null}
            disabledReason={reason}
            onClick={() => {
              if (selectedKey !== null) actions.markCopy(pair, selectedKey)
            }}
            actionId="duplicates.source.mark-copy"
          >
            Это копия
          </Button>
        )}
        {pair.state === 'marked' && (
          <Button variant="secondary" icon={<Undo2 size={16} aria-hidden="true" />} busy={busy} onClick={() => actions.reset(pair)} actionId="duplicates.source.unmark">
            Снять отметку
          </Button>
        )}
        {pair.state !== 'not-duplicate' && (
          <Button variant="secondary" icon={<CircleSlash size={16} aria-hidden="true" />} busy={busy} onClick={() => actions.notDuplicate(pair)} actionId="duplicates.source.not-duplicate">
            Не дубли
          </Button>
        )}
        {pair.state === 'not-duplicate' && (
          <Button variant="secondary" icon={<Undo2 size={16} aria-hidden="true" />} busy={busy} onClick={() => actions.reset(pair)} actionId="duplicates.source.restore">
            Вернуть в список
          </Button>
        )}
        {!decided && selectedKey !== null && <span className="dup__note">Выбранный товар станет копией, второй — оригиналом.</span>}
      </div>
      {error !== null && <ErrorBlock error={error} />}
    </li>
  )
}
